import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType} from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { catchError, debounceTime, distinctUntilChanged, filter, first, map, of, switchMap, tap, withLatestFrom } from 'rxjs';

import * as fromRouter from '@app/root-store/router';
import * as fromActions from './related-news.actions';
import * as fromSelectors from './related-news.selectors';
import * as fromReducer from './related-news.reducer';
import { ChannelService } from '@app/channel/channel.service';
import { NewsService } from '@app/news/news.service';
import { RdsDialogService } from '@rds/angular-components';
import { PreviewDialogComponent, PreviewDialogData } from '@app/utils/preview-dialog/preview-dialog.component';
import { RelatedNewsDialogComponent } from '@app/utils/related-news-dialog/related-news-dialog.component';

@Injectable()
export class RelatedNewsEffects {

  public initRelatedNews$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.initRelatedNews),
      switchMap(() => this.store$.pipe(
          select(fromSelectors.selectRelatedNewsRequestData),
          debounceTime(300),
          distinctUntilChanged((prev, next) => {
            if (prev.resetIndex && !next.resetIndex) {
              delete prev.resetIndex
              delete next.resetIndex
            }
            return JSON.stringify(prev) === JSON.stringify(next)
          }),
          map(({filters, pageIndex, pageSize, sort, resetIndex}) => ({
            filters, 
            sort,
            pageIndex: resetIndex ? 0 : pageIndex,
            pageSize
          }))
        )
      ),
      map((requestData) => fromActions.getRelatedNewsRequest(requestData))
    ), { dispatch: true}
  );

  public getRelatedNewsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getRelatedNewsRequest),
      switchMap((requestData) => this.newsService.getRelatedNews(requestData).pipe(
        map(res => ({
          data: res.data, pagination: res.pagination
        }))
      )),
      map(({data, pagination}) => fromActions.getRelatedNewsSuccess({data, pagination})),
      catchError(({ message }) => of(fromActions.getRelatedNewsFailure({ error: message })))

    ), { dispatch: true}
  );

  public fetchRelatedNewsRequest$ = createEffect(() =>
  this.actions$.pipe(
    ofType(fromActions.fetchRelatedNewsRequest),
    switchMap(({ids}) => this.newsService.getMultipleNewsInfo({ids}).pipe(
      map(res => ({ news: res
      }))
    )),
    map(({news}) => fromActions.addRelatedNews({news})),
  ), { dispatch: true}
);

public openNewsPreview$ = createEffect(() =>
  this.actions$.pipe(
    ofType(fromActions.openNewsPreview),
    switchMap(({newsId}) => this.newsService.get(newsId).pipe(
      map((news) => ({news}))
    )),
    tap(({news}) => {
      const data: PreviewDialogData = {
        news,
        channel: null,
        context: 'NewsSelect'
      }
      this.dialogService.open(PreviewDialogComponent, {
        complexDialog: true,
        size: 'xl',
        height: '100%',
        maxHeight: '100%',
        data
      }
      )
    } )
  ), { dispatch: false}
);

public openRelatedNewsDialog$ = createEffect(() =>
  this.actions$.pipe(
    ofType(fromActions.openRelatedNewsDialog),
    tap(() => {
      this.dialogService.open(RelatedNewsDialogComponent, {
        complexDialog: true,
        size: 'xl',
        height: 'unset',
        maxHeight: 'unset',
      }
      )
    } )
  ), { dispatch: false}
);
  constructor(
    private actions$: Actions,
    private store$: Store<fromReducer.State>,
    private channelService: ChannelService,
    private newsService: NewsService,
    private dialogService: RdsDialogService
  ) {}
}
